// A one-field dialog: rename a step, name a template. Orbit's PromptDialog
// without i18n. Enter submits the trimmed value, Esc or a click outside
// cancels, and focus goes back to whatever opened it.
//
//   <PromptDialog open title="Rename step" placeholder="Step name" defaultValue={name}
//     confirmLabel="Rename" onSubmit={(value) => ...} onClose={() => ...} />
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PromptDialogProps {
  open: boolean;
  title: string;
  /** One plain line under the title. */
  hint?: string;
  placeholder?: string;
  /** The field's value each time the dialog opens. */
  defaultValue?: string;
  /** The submit button's words. Default "Save". */
  confirmLabel?: string;
  /** Gets the trimmed value. The caller closes the dialog (or not). */
  onSubmit: (value: string) => void;
  onClose: () => void;
}

export function PromptDialog({ open, title, hint, placeholder, defaultValue = '', confirmLabel = 'Save', onSubmit, onClose }: PromptDialogProps) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <PromptBody title={title} hint={hint} placeholder={placeholder} defaultValue={defaultValue}
      confirmLabel={confirmLabel} onSubmit={onSubmit} onClose={onClose} />,
    document.body,
  );
}

function PromptBody({ title, hint, placeholder, defaultValue, confirmLabel, onSubmit, onClose }: Omit<PromptDialogProps, 'open'> & { defaultValue: string; confirmLabel: string }) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    inputRef.current?.focus({ preventScroll: true });
    inputRef.current?.select();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (back?.isConnected && back !== document.body) back.focus?.({ preventScroll: true });
    };
  }, []);

  const submit = () => onSubmit(value.trim());
  return (
    <div className="pe-scope">
      <div className="pe-dialog-scrim" aria-hidden="true" onMouseDown={onClose} />
      <div className="pe-dialog-wrap">
        <form role="dialog" aria-modal="true" aria-labelledby={titleId} className="pe-dialog" data-prompt
          onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <h2 id={titleId}>{title}</h2>
          {hint && <p className="pe-dialog-body">{hint}</p>}
          <input ref={inputRef} className="input mt-3" value={value} placeholder={placeholder} aria-label={title}
            autoComplete="off" onChange={(e) => setValue(e.target.value)} />
          <div className="pe-dialog-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">{confirmLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
